'use client'

import type { StickerTakeoverData } from '@/app/util/sticker-takeover'
import UserContext from '@/context/user-context'
import { setGADataLayer } from 'analytics'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

type StickerNaturalSize = { width: number; height: number }

const stickerCount = 6 // the number of stickers to show, each sticker gets a column, inside the column there is a random x-axis shift
const defaultStickerSize = 100 // the size of the sticker, each sticker gets scaled randomly between 1 and 1.5
const maxDelay = 8 // the start animation is delayed, making the stickers stagger in

const getNumBetween = (min: number, max: number) => Math.random() * (max - min) + min
// random sticker id
const getId = (ids: string[]): string => ids[Math.floor(Math.random() * ids.length)]
// how long it takes to fall
const getDuration = () => getNumBetween(4, 12)
// scale between 1 and 1.5
const getSize = () => getNumBetween(1, 1.5)
const getDelay = () => Math.random() * maxDelay
// random x shift within column
const getShift = () => `${Math.floor(Math.random() * 50)}%`
const getStickerRenderHeight = (stickerSize: number, naturalSize?: StickerNaturalSize) =>
    naturalSize?.width && naturalSize.height ? stickerSize * (naturalSize.height / naturalSize.width) : 0
const getStickerStartOffset = (stickerRenderHeight: number, scale: number) =>
    Math.ceil((stickerRenderHeight * (1 + scale)) / 2)

// this will be part of the admin
const sizeOverride: Record<string, number> = {
    gszTGH6PWQqtcOfJOV: 200,
}

const Sticker = ({
    columnWidth,
    columnStart,
    ids,
}: { columnWidth: string; columnStart: string } & StickerTakeoverData) => {
    const animationDuration = useRef(getDuration())
    const animationDelay = useRef(getDelay())
    const scale = useRef(getSize())
    const shift = useRef(getShift())
    const imageRef = useRef<HTMLImageElement>(null)
    const [count, setCount] = useState(0)
    const [naturalSize, setNaturalSize] = useState<StickerNaturalSize>()
    const id = useRef(ids[Math.floor(Math.random() * ids.length)])
    const setLoadedNaturalSize = useCallback((image: HTMLImageElement) => {
        if (image.naturalWidth && image.naturalHeight) {
            setNaturalSize({
                width: image.naturalWidth,
                height: image.naturalHeight,
            })
        }
    }, [])
    const stickerSize = sizeOverride[id.current] || defaultStickerSize
    const stickerRenderHeight = getStickerRenderHeight(stickerSize, naturalSize)
    const stickerStartOffset = getStickerStartOffset(stickerRenderHeight, scale.current)
    const isReady = stickerStartOffset > 0
    useEffect(() => {
        const image = imageRef.current
        if (image?.complete) {
            setLoadedNaturalSize(image)
        }
    }, [count, setLoadedNaturalSize])
    return (
        <div
            key={count}
            className={isReady ? 'absolute animate-[drop_ease-in-out_forwards]' : 'absolute'}
            style={{
                animationDuration: `${animationDuration.current}s`,
                animationDelay: `${animationDelay.current}s`,
                top: `-${stickerStartOffset}px`,
                left: columnStart,
                width: `${columnWidth}`,
                height: `calc(100% + ${stickerStartOffset}px)`,
                visibility: isReady ? undefined : 'hidden',
            }}
            onAnimationEnd={() => {
                animationDuration.current = getDuration()
                animationDelay.current = getDelay()
                id.current = getId(ids)
                scale.current = getSize()
                shift.current = getShift()
                setNaturalSize(undefined)
                setCount((currentCount) => currentCount + 1)
            }}
        >
            <img
                ref={imageRef}
                onLoad={(event) => {
                    setLoadedNaturalSize(event.currentTarget)
                }}
                onClick={() => {
                    setGADataLayer({
                        event: 'attribution_open_click',
                        options: {
                            content: {
                                contentId: id.current,
                                contentType: 'sticker',
                            },
                        },
                    })
                }}
                src={`https://media.giphy.com/media/${id.current}/giphy.webp`}
                width={stickerSize}
                height={Math.round(stickerRenderHeight || stickerSize)}
                className="pointer-events-auto relative"
                style={{
                    left: shift.current,
                    transform: `scale(${scale.current})`,
                    width: `${stickerSize}px`,
                    height: 'auto',
                }}
                alt=""
            />
        </div>
    )
}
const Stickers = ({ ids }: StickerTakeoverData) => {
    const stickers = new Array(stickerCount).fill(0)
    const { user: _user } = useContext(UserContext)
    const [isMounted, setIsMounted] = useState(false)
    useEffect(() => {
        setIsMounted(true)
        setGADataLayer({
            event: 'sticker_takeover_view',
        })
    }, [])

    if (!isMounted) {
        return null
    }

    return (
        <div className="ci-hidden pointer-events-none fixed inset-0 z-modal">
            {stickers.map((_, index) => (
                <Sticker
                    ids={ids}
                    key={index}
                    columnStart={`${Math.floor((100 / stickers.length) * index)}%`}
                    columnWidth={`${100 / stickers.length}%`}
                />
            ))}
        </div>
    )
}
export default Stickers
